// © 2025 Camp Promethean, LLC. All Rights Reserved.
// Trait Radar Chart Component

import { useEffect, useRef } from 'react';
import { EnhancedSelection } from '@/types/selection';

export interface RadarMetric {
  label: string;
  value: number;
}

interface TraitRadarChartProps {
  selection: EnhancedSelection;
  width?: number;
  height?: number;
  className?: string;
  showLabels?: boolean;
  showLegend?: boolean;
  compareTo?: EnhancedSelection;
  maxValue?: number;
  // Add support for custom metrics and metric selection
  customMetrics?: RadarMetric[];
  visibleMetrics?: string[]; // Array of metric labels to display
}

// Helper function to standardize trait names
export const standardizeTraitName = (name: string): string => {
  // Convert shortened trait names to full names
  if (name === 'Aroma' || name === 'Aroma Int.') return 'Aroma Intensity';
  if (name === 'Aroma Qual.') return 'Aroma Quality';
  if (name === 'Flavor' || name === 'Flavor Int.') return 'Flavor Intensity';
  if (name === 'Flavor Qual.') return 'Flavor Quality';
  if (name === 'Smoke' || name === 'Smoke Qual.') return 'Smoke Quality';
  if (name === 'Overall') return 'Overall Quality';
  
  return name;
};

// Helper function to normalize trait name for comparison
export const normalizeTraitName = (name: string): string => {
  return name.toLowerCase().replace(/\s+/g, '');
};

export function TraitRadarChart({
  selection,
  width = 300,
  height = 300,
  className = '',
  showLabels = true,
  showLegend = false,
  compareTo,
  maxValue = 10,
  customMetrics = [],
  visibleMetrics
}: TraitRadarChartProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  
  // Primary and comparison colors
  const primaryColor = '#3b82f6'; // Blue
  const comparisonColor = '#ef4444'; // Red
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    // Set canvas size with proper scaling for high DPI displays
    const scale = window.devicePixelRatio || 1;
    canvas.width = width * scale;
    canvas.height = height * scale;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    ctx.scale(scale, scale);
    
    // Clear canvas
    ctx.clearRect(0, 0, width, height);
    
    // Calculate center and radius
    const centerX = width / 2;
    const centerY = height / 2;
    const radius = Math.min(centerX, centerY) * 0.7; // Slightly smaller for better labels
    
    // Get metrics from selection
    let metrics: Array<{ label: string; value: number }> = [
      { label: 'Structure', value: selection.growthMetrics.structure[0] },
      { label: 'Vigor', value: selection.growthMetrics.vigor[0] },
      { label: 'Yield', value: selection.growthMetrics.yield[0] },
      { label: 'Frost', value: selection.growthMetrics.frost[0] },
      { label: 'Overall Quality', value: selection.growthMetrics.overall[0] },
    ];
    
    // Add sensory metrics if they exist
    if (selection.sensoryData) {
      if (selection.sensoryData.aroma_intensity) {
        metrics.push({ label: 'Aroma Intensity', value: selection.sensoryData.aroma_intensity });
      }
      if (selection.sensoryData.aroma_quality) {
        metrics.push({ label: 'Aroma Quality', value: selection.sensoryData.aroma_quality });
      }
      if (selection.sensoryData.flavor_intensity) {
        metrics.push({ label: 'Flavor Intensity', value: selection.sensoryData.flavor_intensity });
      }
      if (selection.sensoryData.flavor_quality) {
        metrics.push({ label: 'Flavor Quality', value: selection.sensoryData.flavor_quality });
      }
      if (selection.sensoryData.smoke_harshness) {
        metrics.push({ label: 'Smoke Quality', value: selection.sensoryData.smoke_harshness });
      }
    }
    
    // Add custom metrics if provided
    if (customMetrics.length > 0) {
      metrics = [...metrics, ...customMetrics];
    }
    
    // Filter metrics if visibleMetrics is provided
    if (visibleMetrics && visibleMetrics.length > 0) {
      // Normalize trait names for comparison (case insensitive, ignore spaces)
      const normalizedVisibleMetrics = visibleMetrics.map(m => 
        normalizeTraitName(standardizeTraitName(m))
      );
      
      metrics = metrics.filter(metric => {
        const normalizedMetricLabel = normalizeTraitName(standardizeTraitName(metric.label));
        return normalizedVisibleMetrics.some(vm => 
          normalizedMetricLabel === vm ||
          vm.includes(normalizedMetricLabel) ||
          normalizedMetricLabel.includes(vm)
        );
      });
    }
    
    // Get comparison metrics if they exist
    let comparisonMetrics: Array<{ label: string; value: number }> = [];
    if (compareTo) {
      // Base comparison metrics
      comparisonMetrics = [
        { label: 'Structure', value: compareTo.growthMetrics.structure[0] },
        { label: 'Vigor', value: compareTo.growthMetrics.vigor[0] },
        { label: 'Yield', value: compareTo.growthMetrics.yield[0] },
        { label: 'Frost', value: compareTo.growthMetrics.frost[0] },
        { label: 'Overall Quality', value: compareTo.growthMetrics.overall[0] },
      ];
      
      // Add sensory metrics for comparison
      if (compareTo.sensoryData) {
        if (compareTo.sensoryData.aroma_intensity) {
          comparisonMetrics.push({ label: 'Aroma Intensity', value: compareTo.sensoryData.aroma_intensity });
        }
        if (compareTo.sensoryData.aroma_quality) {
          comparisonMetrics.push({ label: 'Aroma Quality', value: compareTo.sensoryData.aroma_quality });
        }
        if (compareTo.sensoryData.flavor_intensity) {
          comparisonMetrics.push({ label: 'Flavor Intensity', value: compareTo.sensoryData.flavor_intensity });
        }
        if (compareTo.sensoryData.flavor_quality) {
          comparisonMetrics.push({ label: 'Flavor Quality', value: compareTo.sensoryData.flavor_quality });
        }
        if (compareTo.sensoryData.smoke_harshness) {
          comparisonMetrics.push({ label: 'Smoke Quality', value: compareTo.sensoryData.smoke_harshness });
        }
      }
      
      // Add custom metrics for comparison if they exist
      if (customMetrics.length > 0 && (compareTo as any).customTraits) {
        customMetrics.forEach(metric => {
          const value = (compareTo as any).customTraits?.[metric.label] || 0;
          if (value) {
            comparisonMetrics.push({ label: metric.label, value });
          }
        });
      }
      
      // Filter comparison metrics to match the main metrics
      const metricLabels = metrics.map(m => m.label);
      comparisonMetrics = comparisonMetrics.filter(m => 
        metricLabels.includes(m.label)
      );
      
      // Ensure the order matches the main metrics
      comparisonMetrics = metricLabels.map(label => {
        const found = comparisonMetrics.find(m => m.label === label);
        return found || { label, value: 0 };
      });
    }
    
    // Check if we have any metrics to display
    if (metrics.length === 0) {
      // Clear canvas and display a message
      ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
      ctx.font = '16px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('No metrics selected to display', centerX, centerY);
      return;
    }
    
    // Calculate angles for each metric
    const metricCount = metrics.length;
    const angleStep = (Math.PI * 2) / metricCount;
    
    // Draw background grid
    drawGrid(ctx, centerX, centerY, radius, metricCount, angleStep);
    
    // Draw axes and labels
    drawAxes(ctx, centerX, centerY, radius, metrics, angleStep);
    
    // Draw selection polygon
    drawPolygon(ctx, centerX, centerY, radius, metrics, maxValue, primaryColor, 0.2);
    
    // Draw selection data points
    drawDataPoints(ctx, centerX, centerY, radius, metrics, maxValue, primaryColor);
    
    // Draw comparison polygon if exists
    if (compareTo && comparisonMetrics.length > 0) {
      drawPolygon(ctx, centerX, centerY, radius, comparisonMetrics, maxValue, comparisonColor, 0.2);
      drawDataPoints(ctx, centerX, centerY, radius, comparisonMetrics, maxValue, comparisonColor);
    }
    
    // Draw legend if enabled
    if (showLegend) {
      const primaryName = selection.id.substring(0, 8);
      const comparisonName = compareTo ? compareTo.id.substring(0, 8) : null;
      drawLegend(ctx, centerX, centerY, radius, primaryName, comparisonName);
    }
    
    // Helper function to draw the background grid
    function drawGrid(
      ctx: CanvasRenderingContext2D,
      centerX: number,
      centerY: number,
      radius: number,
      metricCount: number,
      angleStep: number
    ) {
      const gridLevels = 5; // Number of concentric circles
      
      // Draw concentric circles
      for (let i = 1; i <= gridLevels; i++) {
        const r = (radius * i) / gridLevels;
        ctx.beginPath();
        ctx.arc(centerX, centerY, r, 0, Math.PI * 2);
        ctx.strokeStyle = 'rgba(0, 0, 0, 0.1)';
        ctx.stroke();
      }
      
      // Draw radial grid lines
      for (let i = 0; i < metricCount; i++) {
        const angle = i * angleStep - Math.PI / 2; // Start from top
        const x = centerX + radius * Math.cos(angle);
        const y = centerY + radius * Math.sin(angle);
        
        ctx.beginPath();
        ctx.moveTo(centerX, centerY);
        ctx.lineTo(x, y);
        ctx.strokeStyle = 'rgba(0, 0, 0, 0.1)';
        ctx.stroke();
      }
    }
    
    // Helper function to draw axes and labels
    function drawAxes(
      ctx: CanvasRenderingContext2D,
      centerX: number,
      centerY: number,
      radius: number,
      metrics: Array<{ label: string; value: number }>,
      angleStep: number
    ) {
      metrics.forEach((metric, i) => {
        const angle = i * angleStep - Math.PI / 2; // Start from top (- PI/2)
        const x = centerX + radius * Math.cos(angle);
        const y = centerY + radius * Math.sin(angle);
        
        // Skip drawing the axis line as it's already drawn in the grid
        
        // Draw label if enabled
        if (showLabels) {
          const labelRadius = radius + 20; // Position a bit further out for better visibility
          const labelX = centerX + labelRadius * Math.cos(angle);
          const labelY = centerY + labelRadius * Math.sin(angle);
          
          ctx.font = '12px Arial';
          ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          
          // Adjust position for better readability based on angle
          if (angle > Math.PI / 4 && angle < Math.PI * 3 / 4) {
            // Bottom labels
            ctx.textAlign = 'center';
            ctx.textBaseline = 'top';
          } else if (angle > Math.PI * 3 / 4 && angle < Math.PI * 5 / 4) {
            // Left labels
            ctx.textAlign = 'right';
            ctx.textBaseline = 'middle';
          } else if (angle > Math.PI * 5 / 4 && angle < Math.PI * 7 / 4) {
            // Top labels
            ctx.textAlign = 'center';
            ctx.textBaseline = 'bottom';
          } else {
            // Right labels
            ctx.textAlign = 'left';
            ctx.textBaseline = 'middle';
          }
          
          // Use standardized trait name for display
          const displayLabel = standardizeTraitName(metric.label);
          ctx.fillText(displayLabel, labelX, labelY);
        }
      });
    }
    
    // Helper function to draw the data polygon
    function drawPolygon(
      ctx: CanvasRenderingContext2D,
      centerX: number,
      centerY: number,
      radius: number,
      metrics: Array<{ label: string; value: number }>,
      maxValue: number,
      color: string,
      alpha: number
    ) {
      ctx.beginPath();
      
      metrics.forEach((metric, i) => {
        const angle = i * angleStep - Math.PI / 2; // Start from top (- PI/2)
        const value = Math.min(metric.value, maxValue); // Cap at maxValue
        const distance = (value / maxValue) * radius;
        const x = centerX + distance * Math.cos(angle);
        const y = centerY + distance * Math.sin(angle);
        
        if (i === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      });
      
      ctx.closePath();
      
      // Convert alpha to hex for fillStyle
      // For example, alpha = 0.2 should be "33" in hex
      let alphaHex;
      try {
        alphaHex = Math.round(alpha * 255).toString(16).padStart(2, '0');
      } catch (e) {
        // Fallback for any error in alpha conversion
        alphaHex = '33';
      }
      
      ctx.fillStyle = `${color}${alphaHex}`;
      ctx.fill();
      
      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.stroke();
    }
    
    // Helper function to draw data points
    function drawDataPoints(
      ctx: CanvasRenderingContext2D,
      centerX: number,
      centerY: number,
      radius: number,
      metrics: Array<{ label: string; value: number }>,
      maxValue: number,
      color: string
    ) {
      metrics.forEach((metric, i) => {
        const angle = i * angleStep - Math.PI / 2; // Start from top (- PI/2)
        const value = Math.min(metric.value, maxValue); // Cap at maxValue
        const distance = (value / maxValue) * radius;
        const x = centerX + distance * Math.cos(angle);
        const y = centerY + distance * Math.sin(angle);
        
        // Draw point
        ctx.beginPath();
        ctx.arc(x, y, 4, 0, Math.PI * 2);
        ctx.fillStyle = 'white';
        ctx.fill();
        ctx.strokeStyle = color;
        ctx.lineWidth = 2;
        ctx.stroke();
      });
    }
    
    // Helper function to draw the legend
    function drawLegend(
      ctx: CanvasRenderingContext2D,
      centerX: number,
      centerY: number,
      radius: number,
      selectionName: string,
      comparisonName: string | null
    ) {
      const legendX = centerX - radius;
      const legendY = centerY + radius + 30;
      
      // Draw selection legend
      ctx.beginPath();
      ctx.rect(legendX, legendY, 15, 15);
      ctx.fillStyle = `${primaryColor}33`; // Using 33 for alpha (0.2)
      ctx.fill();
      ctx.strokeStyle = primaryColor;
      ctx.lineWidth = 2;
      ctx.stroke();
      
      ctx.font = '12px Arial';
      ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.fillText(selectionName, legendX + 20, legendY + 7.5);
      
      // Draw comparison legend if comparison exists
      if (comparisonName) {
        ctx.beginPath();
        ctx.rect(legendX + 100, legendY, 15, 15);
        ctx.fillStyle = `${comparisonColor}33`; // Using 33 for alpha (0.2)
        ctx.fill();
        ctx.strokeStyle = comparisonColor;
        ctx.lineWidth = 2;
        ctx.stroke();
        
        ctx.fillText(comparisonName, legendX + 120, legendY + 7.5);
      }
    }
  }, [selection, compareTo, width, height, showLabels, showLegend, maxValue, customMetrics, visibleMetrics]); // Make sure all dependencies are listed
  
  return (
    <canvas
      ref={canvasRef}
      className={className}
      style={{ width: `${width}px`, height: `${height}px` }}
    />
  );
} 